<template>
  <div class="assignment-manager">
    <!-- Assignment Manager Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="fw-bold">Assignment Manager</h4>
      <button 
        class="btn btn-primary"
        @click="showCreateModal = true"
      >
        <i class="bi bi-plus-lg me-2"></i>Create Assignment
      </button>
    </div>

    <!-- Filter and Search Bar -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="input-group">
          <span class="input-group-text">
            <i class="bi bi-search"></i>
          </span>
          <input 
            type="text" 
            class="form-control" 
            placeholder="Search assignments..."
            v-model="searchQuery"
          >
        </div>
      </div>
      <div class="col-md-3">
        <select class="form-select" v-model="selectedSubject">
          <option value="">All Subjects</option>
          <option value="Math">Math</option>
          <option value="Science">Science</option>
          <option value="English">English</option>
          <option value="Reading">Reading</option>
          <option value="Life Skills">Life Skills</option>
        </select>
      </div>
      <div class="col-md-3">
        <select class="form-select" v-model="selectedStatus">
          <option value="">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Active">Active</option>
          <option value="Completed">Completed</option>
          <option value="Archived">Archived</option>
        </select>
      </div>
    </div>

    <!-- Assignments Grid -->
    <div class="row">
      <div 
        v-for="assignment in filteredAssignments" 
        :key="assignment.id"
        class="col-lg-6 col-xl-4 mb-4"
      >
        <div class="modern-card assignment-card h-100">
          <div class="card-header d-flex justify-content-between align-items-start">
            <div>
              <h6 class="fw-bold mb-1">{{ assignment.title }}</h6>
              <small class="text-muted">{{ assignment.subject }}</small>
            </div>
            <div class="dropdown">
              <button 
                class="btn btn-sm btn-outline-secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
              >
                <i class="bi bi-three-dots"></i>
              </button>
              <ul class="dropdown-menu">
                <li><a class="dropdown-item" href="#" @click="editAssignment(assignment)">
                  <i class="bi bi-pencil me-2"></i>Edit
                </a></li>
                <li><a class="dropdown-item" href="#" @click="duplicateAssignment(assignment)">
                  <i class="bi bi-files me-2"></i>Duplicate
                </a></li>
                <li><a class="dropdown-item" href="#" @click="viewSubmissions(assignment)">
                  <i class="bi bi-eye me-2"></i>View Submissions
                </a></li>
                <li><hr class="dropdown-divider"></li>
                <li><a class="dropdown-item text-danger" href="#" @click="deleteAssignment(assignment)">
                  <i class="bi bi-trash me-2"></i>Delete
                </a></li>
              </ul>
            </div>
          </div>
          
          <div class="card-body">
            <p class="text-muted small mb-3">{{ assignment.description }}</p>
            
            <!-- Assignment Stats -->
            <div class="row mb-3">
              <div class="col-6">
                <div class="text-center">
                  <h6 class="fw-bold text-primary">{{ assignment.submissions }}</h6>
                  <small class="text-muted">Submissions</small>
                </div>
              </div>
              <div class="col-6">
                <div class="text-center">
                  <h6 class="fw-bold text-success">{{ assignment.graded }}</h6>
                  <small class="text-muted">Graded</small>
                </div>
              </div>
            </div>

            <!-- Due Date -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span class="small text-muted">Due Date:</span>
              <span class="small fw-bold" :class="getDueDateClass(assignment.dueDate)">
                {{ formatDate(assignment.dueDate) }}
              </span>
            </div>

            <!-- Status Badge -->
            <div class="d-flex justify-content-between align-items-center">
              <span 
                class="badge"
                :class="getStatusBadgeClass(assignment.status)"
              >
                {{ assignment.status }}
              </span>
              <div class="btn-group btn-group-sm">
                <button 
                  class="btn btn-outline-primary"
                  @click="viewAssignment(assignment)"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button 
                  class="btn btn-outline-secondary"
                  @click="editAssignment(assignment)"
                >
                  <i class="bi bi-pencil"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Assignment Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showCreateModal, 'd-block': showCreateModal }"
      tabindex="-1"
      v-if="showCreateModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create New Assignment</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showCreateModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="createAssignment">
              <div class="row">
                <div class="col-md-8">
                  <div class="mb-3">
                    <label class="form-label">Assignment Title *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="newAssignment.title"
                      required
                    >
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Subject *</label>
                    <select class="form-select" v-model="newAssignment.subject" required>
                      <option value="">Select Subject</option>
                      <option value="Math">Math</option>
                      <option value="Science">Science</option>
                      <option value="English">English</option>
                      <option value="Reading">Reading</option>
                      <option value="Life Skills">Life Skills</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea 
                  class="form-control" 
                  rows="3"
                  v-model="newAssignment.description"
                ></textarea>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Due Date *</label>
                    <input 
                      type="datetime-local" 
                      class="form-control" 
                      v-model="newAssignment.dueDate"
                      required
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Points</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model="newAssignment.points"
                      min="0"
                    >
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Assignment Type</label>
                    <select class="form-select" v-model="newAssignment.type">
                      <option value="task">Task</option>
                      <option value="quiz">Quiz</option>
                      <option value="project">Project</option>
                      <option value="reading">Reading</option>
                      <option value="exercise">Exercise</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Difficulty</label>
                    <select class="form-select" v-model="newAssignment.difficulty">
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Assign To</label>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="assignToAll"
                        v-model="newAssignment.assignToAll"
                      >
                      <label class="form-check-label" for="assignToAll">
                        All Students
                      </label>
                    </div>
                  </div>
                  <div class="col-md-6" v-if="!newAssignment.assignToAll">
                    <select class="form-select" multiple v-model="newAssignment.selectedStudents">
                      <option 
                        v-for="student in students" 
                        :key="student.id"
                        :value="student.id"
                      >
                        {{ student.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="showCreateModal = false"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="createAssignment"
            >
              Create Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showCreateModal"></div>
  </div>
</template>

<script>
export default {
  name: 'AssignmentManager',
  data() {
    return {
      showCreateModal: false,
      searchQuery: '',
      selectedSubject: '',
      selectedStatus: '',
      assignments: [],
      students: [],
      newAssignment: {
        title: '',
        subject: '',
        description: '',
        dueDate: '',
        points: 50,
        type: 'task',
        difficulty: 'medium',
        assignToAll: true,
        selectedStudents: []
      }
    }
  },
  async mounted() {
    try {
      const [{ listAssignments }] = await Promise.all([
        import('../services/assignmentService.js')
      ])
      const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
      const userId = user?.id || 0
      // Apply initial server-side filters if desired via query params (kept empty here)
      const data = await listAssignments(userId)
      this.assignments = (Array.isArray(data) ? data : []).map(a => ({
        id: a.id,
        title: a.title,
        subject: a.type === 'reading' ? 'Reading' : (a.type || 'Task'),
        description: a.description,
        dueDate: a.due_date ? new Date(a.due_date) : new Date(),
        status: a.status || 'Active',
        submissions: 0,
        graded: 0,
        points: a.points || 0,
        type: a.type || 'task'
      }))
    } catch (e) {
      console.error('Failed to load assignments:', e)
    }
  },
  computed: {
    filteredAssignments() {
      return this.assignments.filter(assignment => {
        const matchesSearch = assignment.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                            assignment.description.toLowerCase().includes(this.searchQuery.toLowerCase())
        const matchesSubject = !this.selectedSubject || assignment.subject === this.selectedSubject
        const matchesStatus = !this.selectedStatus || assignment.status === this.selectedStatus
        
        return matchesSearch && matchesSubject && matchesStatus
      })
    }
  },
  methods: {
    async createAssignment() {
      if (!this.newAssignment.title || !this.newAssignment.subject || !this.newAssignment.dueDate) {
        this.showMessage('Please fill in all required fields', 'error')
        return
      }

      try {
        const { createAssignment } = await import('../services/assignmentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id || 0
        const payload = {
          title: this.newAssignment.title,
          description: this.newAssignment.description,
          due_date: this.newAssignment.dueDate?.split('T')[0],
          status: 'Active',
          priority: 1,
          difficulty: this.newAssignment.difficulty === 'easy' ? 1 : this.newAssignment.difficulty === 'hard' ? 3 : 2,
          points: this.newAssignment.points,
          type: this.newAssignment.type,
          instructions: null,
          comments: null,
          teacher_id: userId,
          class_code: null
        }
        const res = await createAssignment(userId, payload)
        if (res?.id) {
          this.assignments.unshift({
            id: res.id,
            title: payload.title,
            subject: payload.type,
            description: payload.description,
            dueDate: new Date(payload.due_date),
            status: 'Active',
            submissions: 0,
            graded: 0,
            points: payload.points,
            type: payload.type
          })
          this.resetNewAssignment()
          this.showCreateModal = false
          this.showMessage('Assignment created successfully! 🎯', 'success')
        } else {
          this.showMessage('Failed to create assignment - no response from server', 'error')
        }
      } catch (e) {
        console.error('Create assignment failed:', e)
        this.showMessage(`Failed to create assignment: ${e.message || 'Unknown error'}`, 'error')
      }
    },

    resetNewAssignment() {
      this.newAssignment = {
        title: '',
        subject: '',
        description: '',
        dueDate: '',
        points: 50,
        type: 'task',
        difficulty: 'medium',
        assignToAll: true,
        selectedStudents: []
      }
    },

    async editAssignment(assignment) {
      // TODO: Replace with proper modal - for now use better prompts
      try {
        const newTitle = prompt('Edit assignment title:', assignment.title)
        if (newTitle === null || !newTitle.trim()) return
        
        const validStatuses = ['Active', 'Draft', 'Completed', 'Archived']
        let newStatus = prompt(`Edit status (${validStatuses.join('/')}):`, assignment.status)
        if (!newStatus || !validStatuses.includes(newStatus)) {
          newStatus = assignment.status
        }
        const { updateAssignment } = await import('../services/assignmentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id || 0
        const payload = { title: newTitle.trim(), status: newStatus.trim() }
        const updated = await updateAssignment(userId, assignment.id, payload)
        // Update local row
        Object.assign(assignment, {
          title: updated.title,
          status: updated.status,
          description: updated.description ?? assignment.description,
          dueDate: updated.due_date ? new Date(updated.due_date) : assignment.dueDate,
          points: updated.points ?? assignment.points,
          type: updated.type ?? assignment.type
        })
        this.showMessage('Assignment updated successfully! ✏️', 'success')
      } catch (e) {
        console.error('Update assignment failed:', e)
        this.showMessage(`Failed to update assignment: ${e.message || 'Unknown error'}`, 'error')
      }
    },

    duplicateAssignment(assignment) {
      const duplicate = { ...assignment, id: Date.now(), title: `${assignment.title} (Copy)`, status: 'Draft', submissions: 0, graded: 0 }
      this.assignments.unshift(duplicate)
      this.showMessage('Assignment duplicated! Remember to save changes.', 'info')
    },

    async deleteAssignment(assignment) {
      if (!confirm(`Are you sure you want to delete "${assignment.title}"?`)) return
      try {
        const { deleteAssignment } = await import('../services/assignmentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id || 0
        await deleteAssignment(userId, assignment.id)
        this.assignments = this.assignments.filter(a => a.id !== assignment.id)
        this.showMessage('Assignment deleted successfully!')
      } catch (e) {
        console.error('Delete assignment failed:', e)
        this.showMessage('Failed to delete assignment')
      }
    },

    viewAssignment(assignment) {
      this.$router.push(`/tasks/${assignment.id}`)
    },

    async viewSubmissions(assignment) {
      try {
        const { listSubmissions } = await import('../services/assignmentService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id || 0
        const subs = await listSubmissions(userId, assignment.id)
        if (!subs || subs.length === 0) {
          this.showMessage('No submissions yet.')
          return
        }
        // Basic viewer for now
        const lines = subs.map(s => `#${s.id} • Student ${s.student_id} • ${s.status || 'Submitted'} • Score: ${s.score ?? '-'} • ${s.submitted_date ?? '-'}`)
        // TODO: Replace with proper modal - for now use improved alert
        const message = `Submissions for "${assignment.title}":\n\n` + lines.join('\n')
        this.showMessage(message.replace(/\n/g, '<br/>'), 'info')
      } catch (e) {
        console.error('List submissions failed:', e)
        this.showMessage('Failed to load submissions')
      }
    },

    formatDate(date) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    getDueDateClass(dueDate) {
      const now = new Date()
      const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) return 'text-danger'
      if (diffDays <= 1) return 'text-warning'
      return 'text-success'
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Active': 'bg-success',
        'Draft': 'bg-secondary',
        'Completed': 'bg-primary',
        'Archived': 'bg-dark'
      }
      return classes[status] || 'bg-secondary'
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.assignment-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.assignment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  padding: 1rem;
}

.card-body {
  padding: 1rem;
}

.modal.show {
  background: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style> 